/**
 * UspsSuggestionPanel — shows the USPS-suggested address line by line next to
 * what was entered (current -> suggested, changed words highlighted), with the
 * "Accept Standardized" button underneath. Nothing changes until the officer
 * clicks it (Collette Cruse life-ring report 2026-10-16; Bill approved
 * 2026-10-16).
 *
 * Used by the person dialog's AddressSection and the RAM Address change action.
 */
import type { CSSProperties } from 'react';
import { Button } from '@/components/primitives';
import { addressDiffRows, addressHasChanges, type AddressLines } from '@/utils/addressDiff';

export interface UspsSuggestionPanelProps {
  current: AddressLines;
  suggested: AddressLines;
  onAccept: () => void;
  /** Disables the accept button (e.g. city missing). */
  acceptDisabled?: boolean;
}

const wrap: CSSProperties = {
  marginTop: 8,
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--warn-text, #856404)',
  background: 'var(--warn-bg, #fff3cd)',
  color: 'var(--ink-800, #2c3e50)',
  fontSize: 12,
};

const cell: CSSProperties = { padding: '2px 6px', verticalAlign: 'top' };

const mark: CSSProperties = {
  background: 'var(--info-bg, #d9edf7)',
  color: 'var(--info-text, #2968ad)',
  fontWeight: 700,
  borderRadius: 3,
  padding: '0 2px',
};

export function UspsSuggestionPanel({
  current,
  suggested,
  onAccept,
  acceptDisabled = false,
}: UspsSuggestionPanelProps): JSX.Element | null {
  const rows = addressDiffRows(current, suggested);
  if (!addressHasChanges(rows)) return null;
  return (
    <div style={wrap} role="region" aria-label="USPS suggested address">
      <div style={{ fontWeight: 600, color: 'var(--warn-text, #856404)', marginBottom: 4 }}>
        USPS suggests a correction. Check it with the customer before accepting. Nothing changes until you click Accept Standardized.
      </div>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr style={{ textAlign: 'left', color: 'var(--ink-600, #5a6672)' }}>
            <th style={cell} scope="col"> </th>
            <th style={cell} scope="col">Entered</th>
            <th style={cell} scope="col" aria-hidden> </th>
            <th style={cell} scope="col">USPS suggests</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} style={{ opacity: r.changed ? 1 : 0.7 }}>
              <th style={{ ...cell, textAlign: 'left', fontWeight: 600 }} scope="row">{r.label}</th>
              <td style={cell}>{r.current || <em>blank</em>}</td>
              <td style={cell} aria-hidden>{r.changed ? '→' : ''}</td>
              <td style={cell}>
                {r.changed
                  ? r.suggestedParts.map((p, i) => (
                    <span key={i}>
                      {i > 0 ? ' ' : ''}
                      {p.changed ? <mark style={mark}>{p.text}</mark> : p.text}
                    </span>
                  ))
                  : <span>no change</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ marginTop: 6 }}>
        <Button variant="green" size="sm" onClick={onAccept} disabled={acceptDisabled}>
          Accept Standardized
        </Button>
      </div>
    </div>
  );
}
